import { countUp, untilOrSkip, toDataRow } from "./chestCollection";
import { baseDiag } from "../../utils/fsDiag";
import {
  HOLD_AND_WIN_ROWS,
  toScreenRow,
} from "../../RGS/Providers/PopiPlay/holdAndWinState";

/**
 * scarabReveal — the Scarabs revealing at settlement (GDD "Scarab", End of
 * Hold & Win step 2), before the End Collector collects.
 *
 * In the server's reveal order (storage.scarabs), one Scarab at a time:
 *
 *   the scarab   plays `win` and its "×N" pops in over it — and stays
 *   its targets  every valued neighbour (sides and diagonals, active rows
 *                only) takes the multiplier:
 *                  coin          plays `win`, its number counts up
 *                  jackpot coin  plays `double_value` — the tier word goes,
 *                                the multiplied number pops in, tier colours
 *                  chest         its number counts up
 *                Two scarabs on one cell multiply together, so a later reveal
 *                counts on from the earlier one's result.
 *
 * Each step waits on the spines' own completion — no timers. A press skips
 * what is left: every cell lands on its final number at once.
 *
 * ── Neighbours are the SERVER's ──
 * Adjacency is read in DATA rows (the server's, ±1 inside the active zone)
 * and drawn on the screen rows those map to (contract §14.1) — so the board
 * shows exactly what the server multiplied.
 *
 * ── The number is the server's ──
 * The walk shows the steps; the end state is values × storage.scarab_
 * multipliers, the product grid the payout was computed from — never a
 * client recount. Returns that grid, for the End Collector to collect.
 */

// A multiplied coin's number counting up, ms (the chests' and collector's).
const COUNT_MS = 150;

/** The grid each cell ends on: value × the server's product for that cell. */
const finalGrid = (values, multipliers) =>
  values.map((column, c) =>
    column.map((v, r) => {
      const m = multipliers?.[c]?.[r];
      return v > 0 && m > 1 ? Math.round(v * m) : v;
    }),
  );

/**
 * @param {object}   p
 * @param {Array}    p.scarabs           holdAndWin.step.scarabs — reveal order
 * @param {Array}    p.values            the server's grid, screen rows
 * @param {Array}    [p.scarabMultipliers] the server's product grid, screen rows
 * @param {number}   p.unlockedRows      active rows (data rows 0..n-1)
 * @param {object}   p.overlay           HoldAndWinOverlay's handle
 * @param {Function} p.isSkipped         true once the player pressed to skip
 * @returns {Promise<Array>} the multiplied grid (screen rows)
 */
export const playScarabReveal = async ({
  scarabs,
  values,
  scarabMultipliers,
  unlockedRows,
  overlay,
  isSkipped,
}) => {
  if (!values) return values;
  const target = finalGrid(values, scarabMultipliers);
  // What the reveal was handed, against what stands on the board: a scarab
  // on the board with no entry is one the server did not list (the simulator
  // leaves out a scarab with no valued neighbour — the GDD says it still
  // reveals).
  let onBoard = 0;
  for (let c = 0; c < values.length; c++) {
    for (let r = 0; r < values[c].length; r++) {
      if (overlay?.rig?.(c, r) === "scr") onBoard += 1;
    }
  }
  baseDiag("H&W scarab reveal", {
    listed: Array.isArray(scarabs) ? scarabs.length : null,
    onBoard,
  });
  if (!Array.isArray(scarabs) || !scarabs.length || !overlay) return target;

  const running = values.map((column) => column.slice());
  const product = values.map((column) => column.map(() => 1));
  const columns = values.length;

  for (const { reel, screenRow, multiplier } of scarabs) {
    if (isSkipped()) break;

    // The scarab: `win`, and its ×N.
    // await untilOrSkip(overlay.revealScarab(reel, screenRow, multiplier), isSkipped);
    // if (isSkipped()) break;
    // ── At the same time as its neighbours ── the scarab's `win` is its
    // glow; the multiplying starts with it, not after it (Vladimir,
    // 2026-09-30: "it should happen all at once").
    const scarabWin = overlay.revealScarab(reel, screenRow, multiplier);

    // Its valued neighbours, in the server's rows.
    const dataRow = toDataRow(screenRow);
    const hits = [];
    for (let dc = -1; dc <= 1; dc++) {
      for (let dr = -1; dr <= 1; dr++) {
        if (!dc && !dr) continue;
        const c = reel + dc;
        const d = dataRow + dr;
        if (c < 0 || c >= columns || d < 0 || d >= unlockedRows || d >= HOLD_AND_WIN_ROWS) {
          continue;
        }
        const r = toScreenRow(d);
        const base = values[c]?.[r];
        if (!(base > 0)) continue;
        product[c][r] *= multiplier;
        const from = running[c][r];
        const to = Math.round(base * product[c][r]);
        running[c][r] = to;
        const rig = overlay.rig(c, r);
        if (rig === "cj") {
          hits.push(overlay.revealJackpotValue(c, r, to));
        } else if (rig === "c") {
          hits.push(
            Promise.all([
              overlay.playWin(c, r),
              countUp(from, to, COUNT_MS, (n) => overlay.setValue(c, r, n)),
            ]),
          );
        } else {
          hits.push(countUp(from, to, COUNT_MS, (n) => overlay.setValue(c, r, n)));
        }
      }
    }
    baseDiag("H&W scarab", {
      cell: `${reel},${screenRow}`,
      multiplier,
      hits: hits.length,
    });
    // await untilOrSkip(Promise.all(hits), isSkipped);
    await untilOrSkip(Promise.all([scarabWin, ...hits]), isSkipped);
  }

  // ── The server's numbers, always ── (and every step a press skipped)
  for (let c = 0; c < columns; c++) {
    for (let r = 0; r < target[c].length; r++) {
      if (target[c][r] === values[c][r]) continue;
      if (overlay.rig(c, r) === "cj") {
        // A jackpot coin not yet turned (a press skipped it) turns now.
        if (running[c][r] !== target[c][r]) {
          overlay.revealJackpotValue(c, r, target[c][r]);
        }
      } else {
        overlay.setValue(c, r, target[c][r]);
      }
    }
  }
  const sum = (grid) =>
    grid.reduce((t, column) => t + column.reduce((a, v) => a + (v > 0 ? v : 0), 0), 0);
  baseDiag("H&W scarab reveal done", { before: sum(values), after: sum(target) });
  // Scarabs a press skipped still show their ×N.
  for (const { reel, screenRow, multiplier } of scarabs) {
    overlay.showScarabMultiplier?.(reel, screenRow, multiplier);
  }
  return target;
};
